import type {
  CashReason,
  LedgerEvent,
  LineItem,
  Product,
  RefundTender,
  RegisterSettings,
  SaleTender,
  StockReason,
} from "./events";
import { QR_PROVIDERS, type CurrencyCode, type QrProvider } from "./money";

/**
 * Ledger engine contract (D-003). Pure functions: same code runs in the browser, the Register DO, and tests.
 *
 * OWNERSHIP: package X1 implements applyEvent fully and adds tests. Other packages code against these
 * exported names and shapes; X1 may add fields and exports but must not rename or remove any.
 */

export interface DayState {
  id: string;
  openedAt: number;
  closedAt: number | null;
  openingFloatMinor: number;
  cashSalesMinor: number;
  cashRefundsMinor: number;
  paidInMinor: number;
  paidOutMinor: number;
  qrSalesMinor: number;
  qrRefundsMinor: number;
  /** Tips taken with cash sales (D-063). In the drawer, but not sales. Net of voids. */
  cashTipsMinor: number;
  /** Tips taken with QR sales (D-063). Net of voids. */
  qrTipsMinor: number;
  countedCashMinor: number | null;
  salesCount: number;
  refundsCount: number;
  voidsCount: number;
  /** Units sold per product this day, for the Z report's top products. Net of voids and item refunds. */
  unitsByProduct: Record<string, number>;
  /** Id of the `register.opened` event that opened this day, if one has been applied (D-X1-2). */
  openedEventId?: string;
  /** Id of the `register.closed` event that closed this day, if one has been applied. */
  closedEventId?: string;
}

export type TransactionStatus = "completed" | "voided" | "partially_refunded" | "refunded";

export interface TransactionView {
  id: string;
  kind: "sale" | "refund";
  eventId: string;
  dayId: string | null;
  deviceId: string;
  occurredAt: number;
  items: LineItem[];
  subtotalMinor: number;
  /** Goods total. For a sale the customer paid `totalMinor + tipMinor`. */
  totalMinor: number;
  /** Tip taken with a sale (D-063), including any change the customer kept (D-066); always 0 for a refund. */
  tipMinor: number;
  /** Change the customer kept after the sale, already counted in `tipMinor` (D-066). 0 when none. */
  keptChangeMinor: number;
  tender: SaleTender | RefundTender;
  status: TransactionStatus;
  /** For refunds: the sale refunded, or null for a blind refund. */
  refundOfId: string | null;
  /** For sales: total refunded so far against this sale. */
  refundedMinor: number;
  customerId: string | null;
}

/** Every movement of physical cash in or out of the drawer (mirrors D1 `cash_movements`, PRD §10.5). */
export type CashMovementType = "open_float" | "paid_in" | "paid_out" | "sale" | "refund" | "void" | "kept_change";

export interface CashMovementView {
  id: string;
  dayId: string | null;
  deviceId: string;
  type: CashMovementType;
  /** Magnitude, always ≥ 0. */
  amountMinor: number;
  /** Signed effect on the drawer: the day's movements sum to `expectedCashMinor(day)`. */
  signedMinor: number;
  reason: CashReason | null;
  transactionId: string | null;
  occurredAt: number;
}

/** `stock.adjusted` reasons plus the three implicit movements the ledger generates itself. */
export type StockMovementReason = StockReason | "sale" | "refund" | "void";

export interface StockMovementView {
  id: string;
  productId: string;
  delta: number;
  reason: StockMovementReason;
  transactionId: string | null;
  deviceId: string;
  occurredAt: number;
}

export interface RegisterState {
  registerId: string | null;
  settings: RegisterSettings;
  products: Record<string, Product>;
  /** Current stock per product id (only meaningful for products with trackStock). May go negative (F-804). */
  stock: Record<string, number>;
  /** Open drawer day, or null when closed. */
  day: DayState | null;
  /** All days by id, including the open one. */
  days: Record<string, DayState>;
  transactions: Record<string, TransactionView>;
  /** Transaction ids newest first. */
  transactionOrder: string[];
  /** Derived: float + cash sales + cash tips − cash refunds + paid-in − paid-out for the open day; 0 when closed (F-204). */
  cashOnHandMinor: number;
  /** Incremented by every config event (products, settings). */
  configVersion: number;
  appliedCount: number;
  lastEventId: string | null;
  /** Ids already applied, for idempotency. */
  appliedIds: Set<string>;
  /** Cash drawer movements in application order (F-602, CSV export). */
  cashMovements: CashMovementView[];
  /** Stock movements in application order (F-307, CSV export). */
  stockMovements: StockMovementView[];
}

export const DEFAULT_SETTINGS: RegisterSettings = {
  name: "My Register",
  headerText: "",
  currency: "USD",
  theme: { accent: "c01", mode: "system" },
  payHandles: {},
};

/** Undo window for `sale.voided` (F-407). The Register DO enforces the same bound (S3). */
export const VOID_WINDOW_MS = 60_000;

export function emptyState(registerId: string | null = null, settings: RegisterSettings = DEFAULT_SETTINGS): RegisterState {
  return {
    registerId,
    settings,
    products: {},
    stock: {},
    day: null,
    days: {},
    transactions: {},
    transactionOrder: [],
    cashOnHandMinor: 0,
    configVersion: 0,
    appliedCount: 0,
    lastEventId: null,
    appliedIds: new Set(),
    cashMovements: [],
    stockMovements: [],
  };
}

/** A fresh drawer day with zeroed totals. */
export function newDay(id: string, openedAt: number, openingFloatMinor = 0): DayState {
  return {
    id,
    openedAt,
    closedAt: null,
    openingFloatMinor,
    cashSalesMinor: 0,
    cashRefundsMinor: 0,
    paidInMinor: 0,
    paidOutMinor: 0,
    qrSalesMinor: 0,
    qrRefundsMinor: 0,
    cashTipsMinor: 0,
    qrTipsMinor: 0,
    countedCashMinor: null,
    salesCount: 0,
    refundsCount: 0,
    voidsCount: 0,
    unitsByProduct: {},
  };
}

/** Expected cash in the drawer for a day (F-202). Cash tips are in the drawer too (D-063). */
export function expectedCashMinor(day: DayState): number {
  return (
    day.openingFloatMinor +
    day.cashSalesMinor +
    day.cashTipsMinor -
    day.cashRefundsMinor +
    day.paidInMinor -
    day.paidOutMinor
  );
}

/** Counted − expected for a closed day; null while the day is still open or uncounted. */
export function varianceMinor(day: DayState): number | null {
  return day.countedCashMinor === null ? null : day.countedCashMinor - expectedCashMinor(day);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function cloneDay(day: DayState): DayState {
  return { ...day, unitsByProduct: { ...day.unitsByProduct } };
}

/**
 * Get a mutable copy of a day inside `next`, creating a placeholder if the day has not been opened yet.
 * Events can arrive before the `register.opened` that created their day (catch-up, merge-import), so a
 * referenced day is always materialised; only an explicit `register.opened` makes it the *current* day.
 */
function touchDay(next: RegisterState, dayId: string, occurredAt: number): DayState {
  const existing = next.days[dayId];
  const day = existing ? cloneDay(existing) : newDay(dayId, occurredAt);
  next.days = { ...next.days, [dayId]: day };
  if (next.day && next.day.id === dayId) next.day = day;
  return day;
}

function pushCash(next: RegisterState, movement: CashMovementView): void {
  next.cashMovements = [...next.cashMovements, movement];
}

/**
 * Stock is recorded for every product unless the product is known and explicitly untracked (D-X1-3).
 * Unknown product ids are tolerated (F-306) so an out-of-order `product.upserted` does not lose movements.
 */
function tracksStock(next: RegisterState, productId: string): boolean {
  const product = next.products[productId];
  return product ? product.trackStock : true;
}

function unitsOf(items: readonly LineItem[]): Map<string, number> {
  const units = new Map<string, number>();
  for (const item of items) units.set(item.productId, (units.get(item.productId) ?? 0) + item.qty);
  return units;
}

function applyStockDeltas(
  next: RegisterState,
  deltas: Map<string, number>,
  reason: StockMovementReason,
  transactionId: string | null,
  event: LedgerEvent,
): void {
  const stock = { ...next.stock };
  const movements: StockMovementView[] = [];
  let changed = false;
  for (const [productId, delta] of deltas) {
    if (delta === 0 || !tracksStock(next, productId)) continue;
    stock[productId] = (stock[productId] ?? 0) + delta;
    movements.push({
      id: `${event.id}:${productId}`,
      productId,
      delta,
      reason,
      transactionId,
      deviceId: event.deviceId,
      occurredAt: event.occurredAt,
    });
    changed = true;
  }
  if (!changed) return;
  next.stock = stock;
  next.stockMovements = [...next.stockMovements, ...movements];
}

function addUnits(day: DayState, items: readonly LineItem[], sign: 1 | -1): void {
  for (const [productId, qty] of unitsOf(items)) {
    const current = day.unitsByProduct[productId] ?? 0;
    const value = current + sign * qty;
    if (value === 0) delete day.unitsByProduct[productId];
    else day.unitsByProduct[productId] = value;
  }
}

/** Newest first: descending occurredAt, then descending id (UUIDv7 ids are time-ordered). */
function compareNewestFirst(a: { occurredAt: number; id: string }, b: { occurredAt: number; id: string }): number {
  if (a.occurredAt !== b.occurredAt) return b.occurredAt - a.occurredAt;
  if (a.id === b.id) return 0;
  return a.id > b.id ? -1 : 1;
}

function insertTransaction(next: RegisterState, transactions: Record<string, TransactionView>, tx: TransactionView): void {
  transactions[tx.id] = tx;
  const order = next.transactionOrder.slice();
  let index = order.length;
  for (let i = 0; i < order.length; i++) {
    const otherId = order[i];
    const other = otherId ? transactions[otherId] : undefined;
    if (!other) continue;
    if (compareNewestFirst(tx, other) < 0) {
      index = i;
      break;
    }
  }
  order.splice(index, 0, tx.id);
  next.transactionOrder = order;
}

// ---------------------------------------------------------------------------
// applyEvent
// ---------------------------------------------------------------------------

/**
 * Apply one event and return the next state. Idempotent by event id (re-applying an id returns the
 * same state object) and never throws on a well-formed event; unknown product ids, unknown day ids and
 * negative stock are tolerated (F-306, F-804).
 */
export function applyEvent(state: RegisterState, event: LedgerEvent): RegisterState {
  if (state.appliedIds.has(event.id)) return state;
  const next: RegisterState = {
    ...state,
    registerId: state.registerId ?? event.registerId,
    appliedCount: state.appliedCount + 1,
    lastEventId: event.id,
    appliedIds: new Set(state.appliedIds).add(event.id),
  };

  switch (event.type) {
    case "product.upserted": {
      next.products = { ...state.products, [event.payload.product.id]: event.payload.product };
      next.configVersion = state.configVersion + 1;
      break;
    }

    case "product.archived": {
      const product = state.products[event.payload.productId];
      if (product) next.products = { ...state.products, [product.id]: { ...product, archivedAt: event.occurredAt } };
      next.configVersion = state.configVersion + 1;
      break;
    }

    case "product.deleted": {
      const id = event.payload.productId;
      if (state.products[id]) {
        const products = { ...state.products };
        delete products[id];
        next.products = products;
      }
      if (id in state.stock) {
        const stock = { ...state.stock };
        delete stock[id];
        next.stock = stock;
      }
      next.configVersion = state.configVersion + 1;
      break;
    }

    case "register.updated": {
      next.settings = { ...state.settings, ...event.payload.patch } as RegisterSettings;
      next.configVersion = state.configVersion + 1;
      break;
    }

    case "register.opened": {
      const { dayId, openingFloatMinor } = event.payload;
      const day = touchDay(next, dayId, event.occurredAt);
      // A second `register.opened` for the same day is a duplicate, not a second float.
      if (day.openedEventId === undefined) {
        day.openedAt = event.occurredAt;
        day.openingFloatMinor = openingFloatMinor;
        day.openedEventId = event.id;
        pushCash(next, {
          id: event.id,
          dayId,
          deviceId: event.deviceId,
          type: "open_float",
          amountMinor: openingFloatMinor,
          signedMinor: openingFloatMinor,
          reason: null,
          transactionId: null,
          occurredAt: event.occurredAt,
        });
      }
      if (day.closedAt === null) next.day = day;
      break;
    }

    case "register.closed": {
      const { dayId, countedCashMinor } = event.payload;
      const day = touchDay(next, dayId, event.occurredAt);
      if (day.closedAt === null) {
        day.closedAt = event.occurredAt;
        day.closedEventId = event.id;
      }
      day.countedCashMinor = countedCashMinor;
      if (next.day && next.day.id === dayId) next.day = null;
      break;
    }

    case "cash.paid_in":
    case "cash.paid_out": {
      const { dayId, amountMinor, reason } = event.payload;
      const day = touchDay(next, dayId, event.occurredAt);
      const paidIn = event.type === "cash.paid_in";
      if (paidIn) day.paidInMinor += amountMinor;
      else day.paidOutMinor += amountMinor;
      pushCash(next, {
        id: event.id,
        dayId,
        deviceId: event.deviceId,
        type: paidIn ? "paid_in" : "paid_out",
        amountMinor,
        signedMinor: paidIn ? amountMinor : -amountMinor,
        reason,
        transactionId: null,
        occurredAt: event.occurredAt,
      });
      break;
    }

    case "sale.completed": {
      const p = event.payload;
      if (next.transactions[p.transactionId]) break; // duplicate transaction id under a new event id
      // Events stored before D-063 are folded from IndexedDB and DO storage without a re-parse, so the
      // schema's default never ran on them.
      const tip = p.tipMinor ?? 0;
      const tx: TransactionView = {
        id: p.transactionId,
        kind: "sale",
        eventId: event.id,
        dayId: p.dayId,
        deviceId: event.deviceId,
        occurredAt: event.occurredAt,
        items: p.items,
        subtotalMinor: p.subtotalMinor,
        totalMinor: p.totalMinor,
        tipMinor: tip,
        keptChangeMinor: 0,
        tender: p.tender,
        status: "completed",
        refundOfId: null,
        refundedMinor: 0,
        customerId: p.customerId,
      };
      const transactions = { ...next.transactions };
      insertTransaction(next, transactions, tx);
      next.transactions = transactions;

      if (p.dayId !== null) {
        const day = touchDay(next, p.dayId, event.occurredAt);
        day.salesCount += 1;
        if (p.tender.type === "cash") {
          day.cashSalesMinor += p.totalMinor;
          day.cashTipsMinor += tip;
        } else {
          day.qrSalesMinor += p.totalMinor;
          day.qrTipsMinor += tip;
        }
        addUnits(day, p.items, 1);
      }

      if (p.tender.type === "cash") {
        // The drawer takes the goods and the tip together (D-063).
        const collected = p.totalMinor + tip;
        pushCash(next, {
          id: event.id,
          dayId: p.dayId,
          deviceId: event.deviceId,
          type: "sale",
          amountMinor: collected,
          signedMinor: collected,
          reason: null,
          transactionId: p.transactionId,
          occurredAt: event.occurredAt,
        });
      }

      const deltas = new Map<string, number>();
      for (const [productId, qty] of unitsOf(p.items)) deltas.set(productId, -qty);
      applyStockDeltas(next, deltas, "sale", p.transactionId, event);
      break;
    }

    case "sale.voided": {
      const tx = next.transactions[event.payload.transactionId];
      if (!tx || tx.kind !== "sale" || tx.status === "voided") break;
      next.transactions = { ...next.transactions, [tx.id]: { ...tx, status: "voided" } };

      if (tx.dayId !== null) {
        const day = touchDay(next, tx.dayId, event.occurredAt);
        day.salesCount -= 1;
        day.voidsCount += 1;
        if (tx.tender.type === "cash") {
          day.cashSalesMinor -= tx.totalMinor;
          day.cashTipsMinor -= tx.tipMinor;
        } else {
          day.qrSalesMinor -= tx.totalMinor;
          day.qrTipsMinor -= tx.tipMinor;
        }
        addUnits(day, tx.items, -1);
      }

      if (tx.tender.type === "cash") {
        const collected = tx.totalMinor + tx.tipMinor;
        pushCash(next, {
          id: event.id,
          dayId: tx.dayId,
          deviceId: event.deviceId,
          type: "void",
          amountMinor: collected,
          signedMinor: -collected,
          reason: null,
          transactionId: tx.id,
          occurredAt: event.occurredAt,
        });
      }

      const deltas = new Map<string, number>();
      for (const [productId, qty] of unitsOf(tx.items)) deltas.set(productId, qty);
      applyStockDeltas(next, deltas, "void", tx.id, event);
      break;
    }

    case "refund.completed": {
      const p = event.payload;
      if (next.transactions[p.refundId]) break;
      // Link back to the original sale (F-503). Blind refunds and unknown originals are tolerated.
      const originalId = p.originalTransactionId;
      const original = originalId ? next.transactions[originalId] : undefined;
      const tx: TransactionView = {
        id: p.refundId,
        kind: "refund",
        eventId: event.id,
        dayId: p.dayId,
        deviceId: event.deviceId,
        occurredAt: event.occurredAt,
        items: p.items,
        subtotalMinor: p.amountMinor,
        totalMinor: p.amountMinor,
        tipMinor: 0,
        keptChangeMinor: 0,
        tender: p.tender,
        status: "completed",
        refundOfId: p.originalTransactionId,
        refundedMinor: 0,
        // D-047: a refund's own row inherits the sale's customer (not a hardcoded null) so a customer's
        // running totals net out refunds against their sales; a blind refund (no original) stays unattributed.
        customerId: original && original.kind === "sale" ? original.customerId : null,
      };
      const transactions = { ...next.transactions };
      insertTransaction(next, transactions, tx);

      if (original && original.kind === "sale") {
        const refundedMinor = original.refundedMinor + p.amountMinor;
        const status: TransactionStatus =
          original.status === "voided"
            ? "voided"
            : refundedMinor >= original.totalMinor
              ? "refunded"
              : refundedMinor > 0
                ? "partially_refunded"
                : original.status;
        transactions[original.id] = { ...original, refundedMinor, status };
      }
      next.transactions = transactions;

      if (p.dayId !== null) {
        const day = touchDay(next, p.dayId, event.occurredAt);
        day.refundsCount += 1;
        if (p.tender.type === "cash") day.cashRefundsMinor += p.amountMinor;
        else day.qrRefundsMinor += p.amountMinor;
        addUnits(day, p.items, -1);
      }

      if (p.tender.type === "cash") {
        pushCash(next, {
          id: event.id,
          dayId: p.dayId,
          deviceId: event.deviceId,
          type: "refund",
          amountMinor: p.amountMinor,
          signedMinor: -p.amountMinor,
          reason: null,
          transactionId: p.refundId,
          occurredAt: event.occurredAt,
        });
      }

      if (p.restock && p.items.length > 0) {
        const deltas = new Map<string, number>();
        for (const [productId, qty] of unitsOf(p.items)) deltas.set(productId, qty);
        applyStockDeltas(next, deltas, "refund", p.refundId, event);
      }
      break;
    }

    case "sale.customerLinked": {
      // Retroactive link/unlink from History (D-046). Unknown or out-of-order transaction ids are
      // tolerated with a no-op, matching this codebase's convention that applyEvent never throws.
      const { transactionId, customerId } = event.payload;
      const tx = next.transactions[transactionId];
      if (!tx) break;
      const transactions = { ...next.transactions, [tx.id]: { ...tx, customerId } };
      // D-047/D-049: a refund row inherits its sale's customer, so a link made AFTER the refund follows
      // through to every refund of that sale — otherwise the customer's running totals would count the
      // sale but never the refund against it.
      for (const other of Object.values(next.transactions)) {
        if (other.kind === "refund" && other.refundOfId === tx.id) transactions[other.id] = { ...other, customerId };
      }
      next.transactions = transactions;
      break;
    }

    case "sale.changeKept": {
      // D-066. Tolerant like the other retroactive events: anything the Register DO would refuse is a no-op.
      const { transactionId, dayId, amountMinor } = event.payload;
      const tx = next.transactions[transactionId];
      if (!tx || keptChangeAvailableMinor(tx) !== amountMinor || tx.dayId !== dayId) break;
      next.transactions = {
        ...next.transactions,
        [tx.id]: { ...tx, tipMinor: tx.tipMinor + amountMinor, keptChangeMinor: amountMinor },
      };
      if (tx.dayId !== null) touchDay(next, tx.dayId, event.occurredAt).cashTipsMinor += amountMinor;
      pushCash(next, {
        id: event.id,
        dayId: tx.dayId,
        deviceId: event.deviceId,
        type: "kept_change",
        amountMinor,
        signedMinor: amountMinor,
        reason: null,
        transactionId: tx.id,
        occurredAt: event.occurredAt,
      });
      break;
    }

    case "stock.adjusted": {
      const { productId, delta, reason } = event.payload;
      applyStockDeltas(next, new Map([[productId, delta]]), reason, null, event);
      break;
    }

    default: {
      const exhaustive: never = event;
      void exhaustive;
      break;
    }
  }

  next.cashOnHandMinor = next.day ? expectedCashMinor(next.day) : 0;
  return next;
}

export function foldEvents(events: Iterable<LedgerEvent>, initial: RegisterState = emptyState()): RegisterState {
  let s = initial;
  for (const e of events) s = applyEvent(s, e);
  return s;
}

// ---------------------------------------------------------------------------
// Derived views
// ---------------------------------------------------------------------------

/** Transactions belonging to one drawer day, newest first. */
export function dayTransactions(state: RegisterState, dayId: string): TransactionView[] {
  const out: TransactionView[] = [];
  for (const id of state.transactionOrder) {
    const tx = state.transactions[id];
    if (tx && tx.dayId === dayId) out.push(tx);
  }
  return out;
}

/** Most recent sales (any status), newest first. */
export function recentSales(state: RegisterState, limit = 20): TransactionView[] {
  const out: TransactionView[] = [];
  if (limit <= 0) return out;
  for (const id of state.transactionOrder) {
    const tx = state.transactions[id];
    if (tx && tx.kind === "sale") {
      out.push(tx);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/**
 * The change a customer could still say they kept (D-066): a cash sale's change, if it is not voided and its
 * change has not already been kept. 0 when there is nothing to keep.
 */
export function keptChangeAvailableMinor(tx: TransactionView): number {
  if (tx.kind !== "sale" || tx.status === "voided" || tx.tender.type !== "cash") return 0;
  if ((tx.keptChangeMinor ?? 0) > 0 || !("changeMinor" in tx.tender)) return 0;
  return tx.tender.changeMinor > 0 ? tx.tender.changeMinor : 0;
}

/** The last sale still inside the 60 s undo window (F-407), or null. */
export function undoableSale(state: RegisterState, now: number = Date.now(), windowMs: number = VOID_WINDOW_MS): TransactionView | null {
  const [last] = recentSales(state, 1);
  if (!last || last.status !== "completed") return null;
  return now - last.occurredAt <= windowMs ? last : null;
}

export interface DayReportTenderLine {
  /** "cash" or "qr:<provider>". */
  key: string;
  type: "cash" | "qr";
  provider: QrProvider | null;
  salesMinor: number;
  refundsMinor: number;
  /** sales − refunds; tips are not sales (D-063). */
  netMinor: number;
  /** Tips taken through this tender (D-063). */
  tipsMinor: number;
  salesCount: number;
  refundsCount: number;
}

export interface DayReportProduct {
  productId: string;
  label: string;
  units: number;
  grossMinor: number;
}

/** Z report for one drawer day (F-602). */
export interface DayReport {
  dayId: string;
  currency: CurrencyCode;
  openedAt: number;
  closedAt: number | null;
  isOpen: boolean;
  openingFloatMinor: number;
  salesCount: number;
  refundsCount: number;
  voidsCount: number;
  /** Sales that were not voided. */
  grossSalesMinor: number;
  refundsMinor: number;
  /** gross − refunds. */
  netSalesMinor: number;
  cashSalesMinor: number;
  cashRefundsMinor: number;
  qrSalesMinor: number;
  qrRefundsMinor: number;
  /** All tips for the day, cash and QR (D-063). Not part of gross or net sales. */
  tipsMinor: number;
  cashTipsMinor: number;
  qrTipsMinor: number;
  paidInMinor: number;
  paidOutMinor: number;
  byTender: DayReportTenderLine[];
  expectedCashMinor: number;
  countedCashMinor: number | null;
  varianceMinor: number | null;
  topProducts: DayReportProduct[];
  transactionIds: string[];
}

export interface DayReportOptions {
  /** How many products to include in `topProducts` (default 10; 0 or less means all). */
  topProductLimit?: number;
}

/** Build the Z report for a day. Returns null when the day is unknown. */
export function dayReport(state: RegisterState, dayId: string, options: DayReportOptions = {}): DayReport | null {
  const day = state.days[dayId];
  if (!day) return null;

  const cash: DayReportTenderLine = {
    key: "cash", type: "cash", provider: null,
    salesMinor: 0, refundsMinor: 0, netMinor: 0, tipsMinor: 0, salesCount: 0, refundsCount: 0,
  };
  const qr = new Map<QrProvider, DayReportTenderLine>();
  const lineFor = (provider: QrProvider): DayReportTenderLine => {
    let line = qr.get(provider);
    if (!line) {
      line = {
        key: `qr:${provider}`, type: "qr", provider,
        salesMinor: 0, refundsMinor: 0, netMinor: 0, tipsMinor: 0, salesCount: 0, refundsCount: 0,
      };
      qr.set(provider, line);
    }
    return line;
  };

  const perProduct = new Map<string, DayReportProduct>();
  const bump = (item: LineItem, sign: 1 | -1) => {
    let entry = perProduct.get(item.productId);
    if (!entry) {
      const product = state.products[item.productId];
      entry = { productId: item.productId, label: product?.label ?? item.labelSnapshot, units: 0, grossMinor: 0 };
      perProduct.set(item.productId, entry);
    }
    entry.units += sign * item.qty;
    entry.grossMinor += sign * item.lineTotalMinor;
  };

  const transactions = dayTransactions(state, dayId);
  const transactionIds: string[] = [];
  for (const tx of transactions) {
    transactionIds.push(tx.id);
    if (tx.kind === "sale" && tx.status === "voided") continue; // voids leave no tender line
    const line = tx.tender.type === "cash" ? cash : lineFor(tx.tender.provider);
    if (tx.kind === "sale") {
      line.salesMinor += tx.totalMinor;
      line.tipsMinor += tx.tipMinor;
      line.salesCount += 1;
      for (const item of tx.items) bump(item, 1);
    } else {
      line.refundsMinor += tx.totalMinor;
      line.refundsCount += 1;
      for (const item of tx.items) bump(item, -1);
    }
  }

  const byTender: DayReportTenderLine[] = [];
  if (cash.salesCount > 0 || cash.refundsCount > 0) byTender.push(cash);
  for (const provider of QR_PROVIDERS) {
    const line = qr.get(provider);
    if (line) byTender.push(line);
  }
  for (const line of byTender) line.netMinor = line.salesMinor - line.refundsMinor;

  const limit = options.topProductLimit ?? 10;
  const topProducts = [...perProduct.values()]
    .filter((p) => p.units !== 0 || p.grossMinor !== 0)
    .sort((a, b) => b.units - a.units || b.grossMinor - a.grossMinor || a.label.localeCompare(b.label));
  const grossSalesMinor = day.cashSalesMinor + day.qrSalesMinor;
  const refundsMinor = day.cashRefundsMinor + day.qrRefundsMinor;

  return {
    dayId: day.id,
    currency: state.settings.currency,
    openedAt: day.openedAt,
    closedAt: day.closedAt,
    isOpen: day.closedAt === null,
    openingFloatMinor: day.openingFloatMinor,
    salesCount: day.salesCount,
    refundsCount: day.refundsCount,
    voidsCount: day.voidsCount,
    grossSalesMinor,
    refundsMinor,
    netSalesMinor: grossSalesMinor - refundsMinor,
    cashSalesMinor: day.cashSalesMinor,
    cashRefundsMinor: day.cashRefundsMinor,
    qrSalesMinor: day.qrSalesMinor,
    qrRefundsMinor: day.qrRefundsMinor,
    tipsMinor: day.cashTipsMinor + day.qrTipsMinor,
    cashTipsMinor: day.cashTipsMinor,
    qrTipsMinor: day.qrTipsMinor,
    paidInMinor: day.paidInMinor,
    paidOutMinor: day.paidOutMinor,
    byTender,
    expectedCashMinor: expectedCashMinor(day),
    countedCashMinor: day.countedCashMinor,
    varianceMinor: varianceMinor(day),
    topProducts: limit > 0 ? topProducts.slice(0, limit) : topProducts,
    transactionIds,
  };
}
